/**
 * Kişiler ve Şirketler sözleşmeleri (WP 2.9). Kararlar: docs/planning/wp-2.9-kapsam.md §3.
 * Bu modül saf fonksiyonlar içerir (API ve web aynı doğrulama/normalizasyonu kullanır); DB'ye veya ağa erişmez.
 * Keşifle doğrulanan (D/G) ve Castinflow ürün kararı (ÜK) olan değerler yorumlarda ayrılır.
 */
import { z } from 'zod';
import { assistantActorSchema } from './assistant-name.js';
import { latitudeSchema, longitudeSchema } from './geo.js';
import { principalSchema } from './org.js';
import { REQUIRED_FIELD_MESSAGE } from './zod-tr.js';
const uuid = z.guid();
// ---------------------------------------------------------------------------
// Tenant lookup'ları: ilişki tipi, kaynak, sektör, bölge (K-6)
// ---------------------------------------------------------------------------
export const LOOKUP_KINDS = ['relation_type', 'source', 'sector', 'region', 'unit'];
/** Kişi/şirket seçenek listesi türleri (ayar ekranı); `unit` ürün ölçü birimidir (WP 2.10 K-9) */
export const CRM_LOOKUP_KINDS = ['relation_type', 'source', 'sector', 'region'];
export const LOOKUP_KEY_RE = /^[a-z][a-z0-9_]{1,39}$/;
export const LOOKUP_APPLIES = ['both', 'contact', 'company'];
/** Tenant başına tür başına en fazla özel değer */
export const MAX_CUSTOM_LOOKUPS = 100;
/** Sistem ölçü birimleri (WP 2.10 K-9) */
export const SYSTEM_UNITS = [
    { key: 'piece', label: 'Adet' },
    { key: 'hour', label: 'Saat' },
    { key: 'day', label: 'Gün' },
    { key: 'kilogram', label: 'Kilogram' },
    { key: 'gram', label: 'Gram' },
    { key: 'liter', label: 'Litre' },
    { key: 'meter', label: 'Metre' },
    { key: 'square_meter', label: 'Metrekare' },
    { key: 'cubic_meter', label: 'Metreküp' },
    { key: 'package', label: 'Paket' },
    { key: 'box', label: 'Kutu' },
];
/**
 * Sistem değerleri (her tenant'ta vardır; silinemez, etiket/sıra/aktiflik tenant satırıyla ezilebilir).
 * İlişki tipleri: tur 2 F15 (`D`) — Kontak / Potansiyel müşteri / Müşteri / Tedarikçi.
 * Kaynaklar: aday kaynakları S19 (`D`) — E-posta / Gelen çağrı / Soğuk arama / Web formu / Diğer.
 * Sektör ve bölge sistem değeri taşımaz (Teamgram sektör adları kaydedilmedi; uydurulmaz).
 */
export const SYSTEM_LOOKUPS = [
    { kind: 'relation_type', key: 'contact', label: 'Kontak', appliesTo: 'both' },
    { kind: 'relation_type', key: 'prospect', label: 'Potansiyel müşteri', appliesTo: 'both' },
    { kind: 'relation_type', key: 'customer', label: 'Müşteri', appliesTo: 'both' },
    { kind: 'relation_type', key: 'supplier', label: 'Tedarikçi', appliesTo: 'both' },
    { kind: 'source', key: 'email', label: 'E-posta', appliesTo: 'both' },
    { kind: 'source', key: 'inbound_call', label: 'Gelen çağrı', appliesTo: 'both' },
    { kind: 'source', key: 'cold_call', label: 'Soğuk arama', appliesTo: 'both' },
    { kind: 'source', key: 'web_form', label: 'Web formu', appliesTo: 'both' },
    { kind: 'source', key: 'other', label: 'Diğer', appliesTo: 'both' },
    // WP 2.10 K-9: ürün ölçü birimleri (ÜK; teknik anahtar ile etiket ayrı, etiket tenant satırıyla ezilebilir)
    ...SYSTEM_UNITS.map((u) => ({
        kind: 'unit',
        key: u.key,
        label: u.label,
        appliesTo: 'both',
    })),
];
export const lookupSchema = z.object({
    kind: z.enum(LOOKUP_KINDS),
    key: z.string().regex(LOOKUP_KEY_RE),
    label: z.string(),
    appliesTo: z.enum(LOOKUP_APPLIES),
    isActive: z.boolean(),
    isSystem: z.boolean(),
    sortOrder: z.number().int(),
});
const lookupLabel = z.string().trim().min(1).max(60);
export const createLookupInputSchema = z
    .object({
    kind: z.enum(LOOKUP_KINDS),
    label: lookupLabel,
    appliesTo: z.enum(LOOKUP_APPLIES).default('both'),
    sortOrder: z.number().int().min(0).max(10_000).optional(),
})
    .strict();
export const updateLookupInputSchema = z
    .object({
    label: lookupLabel.optional(),
    isActive: z.boolean().optional(),
    sortOrder: z.number().int().min(0).max(10_000).optional(),
    appliesTo: z.enum(LOOKUP_APPLIES).optional(),
})
    .strict()
    .refine((v) => Object.keys(v).length > 0, { message: 'Değişiklik yok' });
/** Sistem değerleri + tenant satırları → birleşik liste (sıra: sortOrder, sonra etiket). */
export function mergeLookups(rows) {
    const out = new Map();
    SYSTEM_LOOKUPS.forEach((s, i) => {
        out.set(`${s.kind}:${s.key}`, {
            kind: s.kind,
            key: s.key,
            label: s.label,
            appliesTo: s.appliesTo,
            isActive: true,
            isSystem: true,
            sortOrder: i * 10,
        });
    });
    for (const r of rows) {
        const id = `${r.kind}:${r.key}`;
        const sys = out.get(id);
        if (sys?.isSystem) {
            out.set(id, {
                ...sys,
                label: r.label ?? sys.label,
                isActive: r.isActive,
                sortOrder: r.sortOrder,
            });
        }
        else {
            out.set(id, {
                kind: r.kind,
                key: r.key,
                label: r.label ?? r.key,
                appliesTo: r.appliesTo,
                isActive: r.isActive,
                isSystem: false,
                sortOrder: r.sortOrder,
            });
        }
    }
    return [...out.values()].sort((a, b) => a.kind.localeCompare(b.kind) ||
        a.sortOrder - b.sortOrder ||
        a.label.localeCompare(b.label, 'tr'));
}
export function lookupAppliesTo(l, type) {
    return l.appliesTo === 'both' || l.appliesTo === type;
}
// ---------------------------------------------------------------------------
// Normalizasyon (K-5, K-7)
// ---------------------------------------------------------------------------
/** C0/C1 kontrol karakterleri (sekme/satır sonu dâhil) — tek satırlık alanlardan temizlenir. */
// eslint-disable-next-line no-control-regex
const CONTROL_RE = /[\u0000-\u001f\u007f-\u009f]/g;
// eslint-disable-next-line no-control-regex
const CONTROL_KEEP_NL_RE = /[\u0000-\u0009\u000b-\u001f\u007f-\u009f]/g;
/** Tek satırlık metin: NFC, kontrol karakteri yok, boşluk sıkıştırılmış, kırpılmış. */
export function cleanLine(s) {
    return s.normalize('NFC').replace(CONTROL_RE, ' ').replace(/\s+/g, ' ').trim();
}
/** Çok satırlı metin: NFC, satır sonu korunur (CRLF → LF), diğer kontrol karakterleri yok. */
export function cleanText(s) {
    return s.normalize('NFC').replace(/\r\n?/g, '\n').replace(CONTROL_KEEP_NL_RE, '').trim();
}
/** Benzer kayıt eşleşmesi için ad: NFC, boşluk sıkıştırma, Türkçe küçük harf (ASCII'ye indirgeme YOK). */
export function normalizeName(s) {
    return cleanLine(s).toLocaleLowerCase('tr-TR');
}
/** Vergi no: büyük harf, boşluksuz (harf/rakam/tire). */
export function normalizeTaxNo(s) {
    return s.normalize('NFC').replace(/\s+/g, '').toLocaleUpperCase('en-US');
}
// ---------------------------------------------------------------------------
// İletişim bilgileri (K-7)
// ---------------------------------------------------------------------------
export const CONTACT_INFO_KINDS = ['phone', 'email', 'address', 'web', 'social'];
/** Alt türler: telefon/e-posta/adres F28 `CI_*` anahtarları (`D`); sosyal F28 + talimat (Skype ÜK). */
export const CONTACT_INFO_SUBTYPES = {
    phone: ['work', 'mobile', 'direct', 'home', 'work_fax', 'home_fax', 'whatsapp', 'other'],
    email: ['work', 'personal', 'other'],
    address: ['work', 'home', 'other'],
    web: ['website'],
    social: ['linkedin', 'facebook', 'x', 'instagram', 'skype'],
};
/** Kayıt başına tür sınırları (makul üst sınır; mükerrer değer ayrıca reddedilir) */
export const CONTACT_INFO_LIMITS = {
    phone: 20,
    email: 20,
    address: 10,
    web: 5,
    social: 10,
};
const optLine = (max) => z
    .string()
    .max(max * 2)
    .transform((s) => cleanLine(s))
    .pipe(z.string().max(max))
    .nullable()
    .optional();
/** İletişim bilgisi alanlarının üst sınırları (API şeması ve arayüz `maxLength` aynı kaynaktan; K-M7-50.4) */
export const CONTACT_INFO_FIELD_MAX = {
    value: 1000,
    province: 80,
    district: 80,
    postalCode: 20,
    addressLine: 400,
};
export const contactInfoInputSchema = z
    .object({
    kind: z.enum(CONTACT_INFO_KINDS),
    subtype: z.string().min(1).max(20),
    /** Görünen değer (adres için isteğe bağlı; parçalardan birleştirilir) */
    value: z.string().max(CONTACT_INFO_FIELD_MAX.value).optional(),
    countryCode: z
        .string()
        .regex(/^[A-Za-z]{2}$/)
        .transform((s) => s.toUpperCase())
        .nullable()
        .optional(),
    province: optLine(CONTACT_INFO_FIELD_MAX.province),
    district: optLine(CONTACT_INFO_FIELD_MAX.district),
    postalCode: optLine(CONTACT_INFO_FIELD_MAX.postalCode),
    addressLine: optLine(CONTACT_INFO_FIELD_MAX.addressLine),
    isPrimary: z.boolean().default(false),
})
    .strict();
export const contactInfoSchema = z.object({
    id: uuid,
    kind: z.enum(CONTACT_INFO_KINDS),
    subtype: z.string(),
    value: z.string(),
    countryCode: z.string().nullable(),
    province: z.string().nullable(),
    district: z.string().nullable(),
    postalCode: z.string().nullable(),
    addressLine: z.string().nullable(),
    isPrimary: z.boolean(),
    sortOrder: z.number().int(),
});
const EMAIL_RE = /^[^\s@<>()[\]\\,;:"]+@[^\s@<>()[\]\\,;:"]+\.[^\s@<>()[\]\\,;:"]{2,}$/;
const HANDLE_RE = {
    x: /^@?[A-Za-z0-9_]{1,15}$/,
    skype: /^[A-Za-z0-9_.:,-]{1,64}$/,
};
/** Yalnız http/https mutlak adres; aksi null. Görünen değer kullanıcının yazdığıdır. */
export function safeHttpUrl(s) {
    try {
        const u = new URL(s);
        return u.protocol === 'http:' || u.protocol === 'https:' ? u : null;
    }
    catch {
        return null;
    }
}
/** Telefon eşleşme anahtarı: baştaki '+' ve rakamlar (ülke kodu EKLENMEZ, görünen değer yeniden yazılmaz). */
export function phoneKey(s) {
    const t = s.trim();
    return (t.startsWith('+') ? '+' : '') + t.replace(/\D/g, '');
}
export function composeAddress(p) {
    const city = [p.postalCode, p.province].filter(Boolean).join(' ');
    return [p.addressLine, p.district, city, p.countryCode].filter((x) => x && x !== '').join(', ');
}
/** Tek iletişim girdisini doğrular + normalize eder. Hata iletileri Türkçe ve değer içermez. */
export function normalizeContactInfo(input) {
    const subtypes = CONTACT_INFO_SUBTYPES[input.kind];
    if (!subtypes.includes(input.subtype))
        return { ok: false, message: 'geçersiz tür' };
    const base = {
        kind: input.kind,
        subtype: input.subtype,
        countryCode: null,
        province: null,
        district: null,
        postalCode: null,
        addressLine: null,
        isPrimary: input.isPrimary,
    };
    if (input.kind === 'address') {
        // Şemadan geçmemiş girdiye karşı da temizlik (kontrol karakteri, boşluk) — savunma derinliği
        const part = (s) => (s ? cleanLine(s) || null : null);
        const parts = {
            countryCode: input.countryCode ? input.countryCode.toUpperCase() : null,
            province: part(input.province),
            district: part(input.district),
            postalCode: part(input.postalCode),
            addressLine: part(input.addressLine),
        };
        if (!parts.addressLine && !parts.district && !parts.province)
            return { ok: false, message: 'adres, ilçe veya il gerekli' };
        const value = composeAddress(parts);
        if (value.length > 500)
            return { ok: false, message: 'adres çok uzun' };
        return {
            ok: true,
            row: { ...base, ...parts, value, valueNorm: normalizeName(value) },
        };
    }
    const raw = cleanLine(input.value ?? '');
    if (raw === '')
        return { ok: false, message: 'değer gerekli' };
    if (raw.length > 500)
        return { ok: false, message: 'değer çok uzun' };
    switch (input.kind) {
        case 'phone': {
            const key = phoneKey(raw);
            const digits = key.replace('+', '');
            if (!/^\+?[0-9 ()./-]+$/.test(raw) || digits.length < 6 || digits.length > 20)
                return { ok: false, message: 'geçersiz telefon' };
            return { ok: true, row: { ...base, value: raw, valueNorm: key } };
        }
        case 'email': {
            if (raw.length > 254 || !EMAIL_RE.test(raw))
                return { ok: false, message: 'geçersiz e-posta' };
            return { ok: true, row: { ...base, value: raw, valueNorm: raw.toLowerCase() } };
        }
        case 'web': {
            const u = safeHttpUrl(raw);
            if (!u)
                return { ok: false, message: 'yalnız http/https adresi' };
            return { ok: true, row: { ...base, value: raw, valueNorm: urlKey(u) } };
        }
        case 'social': {
            const handle = HANDLE_RE[input.subtype];
            if (handle?.test(raw) === true)
                return {
                    ok: true,
                    row: { ...base, value: raw, valueNorm: raw.replace(/^@/, '').toLowerCase() },
                };
            const u = safeHttpUrl(raw);
            if (!u)
                return { ok: false, message: 'yalnız http/https adresi' };
            return { ok: true, row: { ...base, value: raw, valueNorm: urlKey(u) } };
        }
    }
}
function urlKey(u) {
    const path = u.pathname.replace(/\/+$/, '');
    return `${u.host.toLowerCase()}${path}${u.search}`.toLowerCase();
}
/**
 * Bir kaydın iletişim listesi: her girdi doğrulanır; aynı tür + normalize değer mükerrerdir; tür başına en fazla bir
 * birincil (hiç yoksa türün ilk satırı birincil olur — belirlenimci); sıra girdi sırasıdır.
 */
export function normalizeContactInfos(inputs) {
    const issues = [];
    const rows = [];
    const seen = new Set();
    const counts = {};
    inputs.forEach((inp, index) => {
        const r = normalizeContactInfo(inp);
        if (!r.ok) {
            issues.push({ index, message: r.message });
            return;
        }
        const k = `${r.row.kind}\u0000${r.row.valueNorm}`;
        if (seen.has(k)) {
            issues.push({ index, message: 'mükerrer değer' });
            return;
        }
        seen.add(k);
        counts[r.row.kind] = (counts[r.row.kind] ?? 0) + 1;
        if ((counts[r.row.kind] ?? 0) > CONTACT_INFO_LIMITS[r.row.kind]) {
            issues.push({ index, message: 'bu türde en fazla kayıt sayısı aşıldı' });
            return;
        }
        rows.push({ ...r.row, sortOrder: rows.length });
    });
    for (const kind of CONTACT_INFO_KINDS) {
        const ofKind = rows.filter((r) => r.kind === kind);
        const primaries = ofKind.filter((r) => r.isPrimary);
        if (primaries.length > 1) {
            issues.push({
                index: inputs.length,
                message: `her türde en fazla bir birincil değer olabilir`,
            });
        }
        else if (primaries.length === 0 && ofKind[0]) {
            ofKind[0].isPrimary = true;
        }
    }
    return { rows, issues };
}
/** Liste projeksiyonu: birincil e-posta ve telefon görünen değerleri. */
export function primaryValues(rows) {
    const pick = (kind) => rows.find((r) => r.kind === kind && r.isPrimary)?.value ??
        rows.find((r) => r.kind === kind)?.value ??
        null;
    return { email: pick('email'), phone: pick('phone') };
}
// ---------------------------------------------------------------------------
// Özel günler (K-8)
// ---------------------------------------------------------------------------
export const SPECIAL_DAY_KINDS = ['birthday', 'anniversary', 'founding', 'other'];
export const SPECIAL_DAYS_MAX = 20;
/** "Yaklaşan özel günler" penceresi (gün, bugün dâhil) — ürün değeri, tek yerde */
export const UPCOMING_SPECIAL_DAYS = 30;
export function isLeapYear(y) {
    return y % 4 === 0 && (y % 100 !== 0 || y % 400 === 0);
}
export function daysInMonth(month, year) {
    if (month === 2)
        return year === undefined || year === null ? 29 : isLeapYear(year) ? 29 : 28;
    return [4, 6, 9, 11].includes(month) ? 30 : 31;
}
export const specialDayInputSchema = z
    .object({
    kind: z.enum(SPECIAL_DAY_KINDS),
    label: z
        .string()
        .max(120)
        .transform((s) => cleanLine(s))
        .pipe(z.string().max(60))
        .nullable()
        .optional(),
    month: z.number().int().min(1).max(12),
    day: z.number().int().min(1).max(31),
    year: z.number().int().min(1800).max(2200).nullable().optional(),
    description: z
        .string()
        .max(1000)
        .transform((s) => cleanText(s))
        .pipe(z.string().max(500))
        .nullable()
        .optional(),
    isActive: z.boolean().default(true),
})
    .strict()
    .superRefine((v, c) => {
    if (v.day > daysInMonth(v.month, v.year ?? null))
        c.addIssue({ code: 'custom', message: 'geçersiz tarih', path: ['day'] });
    if (v.kind === 'other' && (v.label ?? '') === '')
        c.addIssue({ code: 'custom', message: 'etiket gerekli', path: ['label'] });
});
export const specialDaySchema = z.object({
    id: uuid,
    kind: z.enum(SPECIAL_DAY_KINDS),
    label: z.string().nullable(),
    month: z.number().int(),
    day: z.number().int(),
    year: z.number().int().nullable(),
    description: z.string().nullable(),
    isActive: z.boolean(),
    sortOrder: z.number().int(),
});
/** AAGG (ay*100+gün) — liste projeksiyonu anahtarı */
export function specialDayMd(month, day) {
    return month * 100 + day;
}
/** Aktif özel günlerin projeksiyon dizisi (tekil, sıralı). */
export function specialDayMdSet(days) {
    return [...new Set(days.filter((d) => d.isActive).map((d) => specialDayMd(d.month, d.day)))].sort((a, b) => a - b);
}
/**
 * Bugünden itibaren `days` günlük pencerenin AAGG değerleri. 29 Şubat kuralı: artık olmayan yılda 28 Şubat pencerede ise
 * 229 da eklenir (29 Şubat'lık özel gün 28 Şubat'ta anılır). Tarihler takvim günüdür (saat dilimi uygulanmış "bugün").
 */
export function upcomingMdWindow(today, days = UPCOMING_SPECIAL_DAYS) {
    const out = [];
    let y = today.year;
    let m = today.month;
    let d = today.day;
    for (let i = 0; i < days; i += 1) {
        out.push(specialDayMd(m, d));
        if (m === 2 && d === 28 && !isLeapYear(y))
            out.push(229);
        d += 1;
        if (d > daysInMonth(m, y)) {
            d = 1;
            m += 1;
            if (m > 12) {
                m = 1;
                y += 1;
            }
        }
    }
    return [...new Set(out)];
}
/** Bir özel günün `today`'den itibaren bir sonraki anılma tarihi (29 Şubat → artık olmayan yılda 28 Şubat). */
export function nextOccurrence(sd, today) {
    const on = (year) => ({
        year,
        month: sd.month,
        day: sd.month === 2 && sd.day === 29 && !isLeapYear(year) ? 28 : sd.day,
    });
    const cand = on(today.year);
    const before = cand.month < today.month || (cand.month === today.month && cand.day < today.day);
    return before ? on(today.year + 1) : cand;
}
/** IANA saat diliminde bugünün takvim tarihi (UTC'ye çevrilmiş zaman damgası kullanılmaz). */
export function todayIn(timeZone, now = new Date()) {
    let parts;
    try {
        parts = new Intl.DateTimeFormat('en-CA', {
            timeZone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        }).formatToParts(now);
    }
    catch {
        parts = new Intl.DateTimeFormat('en-CA', {
            timeZone: 'UTC',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        }).formatToParts(now);
    }
    const get = (t) => Number(parts.find((p) => p.type === t)?.value ?? '0');
    return { year: get('year'), month: get('month'), day: get('day') };
}
// ---------------------------------------------------------------------------
// Çekirdek alanlar (K-2, K-3, K-4)
// ---------------------------------------------------------------------------
export const CRM_RECORD_TYPES = ['contact', 'company'];
export function isCrmRecordType(t) {
    return CRM_RECORD_TYPES.includes(t);
}
export const GENDERS = ['female', 'male', 'other'];
/** İYS izin kaynakları — İYS API 3.2.1 (apidocs.iys.org.tr); HS_KARAR yalnız RET durumu içindir, izin kaydına girmez. */
export const IYS_CONSENT_SOURCES = [
    'HS_FIZIKSEL_ORTAM',
    'HS_ISLAK_IMZA',
    'HS_WEB',
    'HS_CAGRI_MERKEZI',
    'HS_SOSYAL_MEDYA',
    'HS_EPOSTA',
    'HS_MESAJ',
    'HS_MOBIL',
    'HS_EORTAM',
    'HS_ETKINLIK',
    'HS_2015',
    'HS_ATM',
];
/** İYS: "1 Mayıs 2015 öncesi" kaynaklı izinlerde izin tarihi yalnız 2015-05-01 olabilir (İYS H155). */
export const IYS_2015_DATE = '2015-05-01';
const line = (max) => z
    .string()
    .max(max * 2)
    .transform((s) => cleanLine(s))
    .pipe(z.string().max(max));
const nullableLine = (max) => line(max)
    .nullable()
    .transform((s) => (s === '' ? null : s));
const longText = z
    .string()
    .max(20_000)
    .transform((s) => cleanText(s))
    .pipe(z.string().max(10_000))
    .nullable()
    .transform((s) => (s === '' ? null : s));
const lookupKey = z.string().regex(LOOKUP_KEY_RE).nullable();
const isoDate = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .refine((s) => !Number.isNaN(Date.parse(`${s}T00:00:00Z`)) &&
    new Date(`${s}T00:00:00Z`).toISOString().startsWith(s), {
    message: 'geçersiz tarih',
})
    .nullable();
const contactCoreShape = {
    firstName: line(100).pipe(z.string().min(1, 'ad gerekli')),
    lastName: line(100).pipe(z.string().min(1, 'soyad gerekli')),
    title: nullableLine(120),
    companyId: uuid.nullable(),
    relationTypes: z.array(z.string().regex(LOOKUP_KEY_RE)).max(10),
    gender: z.enum(GENDERS).nullable(),
    details: longText,
    identityNo: z
        .string()
        .max(40)
        .transform((s) => s.replace(/\s+/g, ''))
        .pipe(z.string().regex(/^[A-Za-z0-9]{0,20}$/, 'geçersiz kimlik no'))
        .nullable()
        .transform((s) => (s === '' ? null : s)),
    taxOffice: nullableLine(120),
    paymentTermDays: z.number().int().min(0).max(3650).nullable(),
    iysConsentAt: isoDate,
    iysConsentSource: z.enum(IYS_CONSENT_SOURCES).nullable(),
    sourceKey: lookupKey,
    sectorKey: lookupKey,
    regionKey: lookupKey,
    isActive: z.boolean(),
    ownerId: uuid.nullable(),
};
const companyCoreShape = {
    name: line(200).pipe(z.string().min(1, 'ad gerekli')),
    tradeName: nullableLine(200),
    relationTypes: z.array(z.string().regex(LOOKUP_KEY_RE)).max(10),
    details: longText,
    taxNo: z
        .string()
        .max(40)
        .transform((s) => normalizeTaxNo(s))
        .pipe(z.string().regex(/^[A-Z0-9-]{0,20}$/, 'geçersiz vergi no'))
        .nullable()
        .transform((s) => (s === '' ? null : s)),
    taxOffice: nullableLine(120),
    paymentTermDays: z.number().int().min(0).max(3650).nullable(),
    location: z.object({ lat: latitudeSchema, lng: longitudeSchema }).strict().nullable(),
    sourceKey: lookupKey,
    sectorKey: lookupKey,
    regionKey: lookupKey,
    isActive: z.boolean(),
    ownerId: uuid.nullable(),
};
export const contactCoreInputSchema = z.object(contactCoreShape).partial().strict();
export const companyCoreInputSchema = z.object(companyCoreShape).partial().strict();
export const CONTACT_INFOS_MAX = 60;
/** Açık paylaşım girdisi (oluşturmada; WP 2.9 K-9, WP 2.10 ürün) */
export const recordSharingInputSchema = z
    .object({
    readers: z.array(principalSchema).max(100).default([]),
    writers: z.array(principalSchema).max(100).default([]),
})
    .strict();
/** Oluşturma gövdesi: çekirdek + iletişim + özel günler + özel alanlar + isteğe bağlı açık paylaşım (K-9). */
export const createCrmRecordInputSchema = z
    .object({
    core: z.record(z.string(), z.unknown()),
    contactInfos: z.array(contactInfoInputSchema).max(CONTACT_INFOS_MAX).default([]),
    specialDays: z.array(specialDayInputSchema).max(SPECIAL_DAYS_MAX).default([]),
    customValues: z.record(z.string(), z.unknown()).prefault({}),
    sharing: recordSharingInputSchema.optional(),
})
    .strict();
/** Güncelleme gövdesi (K-10): verilen bölümler değişir; `contactInfos`/`specialDays` verilirse tam liste değiştirilir. */
export const updateCrmRecordInputSchema = z
    .object({
    core: z.record(z.string(), z.unknown()).optional(),
    contactInfos: z.array(contactInfoInputSchema).max(CONTACT_INFOS_MAX).optional(),
    specialDays: z.array(specialDayInputSchema).max(SPECIAL_DAYS_MAX).optional(),
    customValues: z.record(z.string(), z.unknown()).optional(),
})
    .strict();
/** Çekirdek alan anahtarı → "Bilgi giriş alanları" anahtarı (zorunluluk ayarı; metadata.ts BUILTIN_FIELD_CATALOG). */
export const CORE_FIELD_SETTING_KEY = {
    contact: {
        firstName: 'firstName',
        lastName: 'lastName',
        relationTypes: 'relationType',
        title: 'title',
        companyId: 'company',
        gender: 'gender',
        details: 'details',
        identityNo: 'identityNo',
        taxOffice: 'taxOffice',
        paymentTermDays: 'paymentTerm',
        iysConsentAt: 'iys',
    },
    company: {
        name: 'name',
        relationTypes: 'relationType',
        tradeName: 'tradeName',
        sourceKey: 'source',
        sectorKey: 'sector',
        regionKey: 'region',
        details: 'details',
        taxNo: 'taxNo',
        taxOffice: 'taxOffice',
        paymentTermDays: 'paymentTerm',
        location: 'location',
    },
};
/**
 * Çekirdek girdi doğrulaması (tip + çapraz kurallar). `requiredSettings`: "Bilgi giriş alanları" ayarında zorunlu olan
 * ayar anahtarları. Oluşturmada (`mode: 'create'`) tüm zorunlu alanlar; güncellemede yalnız gövdede gelen alanlar
 * denetlenir (sonradan zorunlu yapılmış boş alan ilgisiz bir düzenlemeyi kilitlemez). `final` = yazım sonrası tam değerler.
 */
export function validateCrmCore(type, raw, opts) {
    const schema = type === 'contact' ? contactCoreInputSchema : companyCoreInputSchema;
    const issues = [];
    const parsed = schema.safeParse(raw);
    if (!parsed.success) {
        for (const i of parsed.error.issues)
            issues.push({
                field: String(i.path[0] ?? 'core'),
                message: i.code === 'unrecognized_keys' ? 'bilinmeyen alan' : i.message,
            });
        return { ok: false, values: {}, issues };
    }
    const values = parsed.data;
    const all = opts.final(values);
    if (type === 'contact') {
        const src = all['iysConsentSource'];
        const at = all['iysConsentAt'];
        if (src === 'HS_2015' && at !== null && at !== undefined && at !== IYS_2015_DATE)
            issues.push({
                field: 'iysConsentAt',
                message: '1 Mayıs 2015 öncesi kaynağında tarih 2015-05-01 olmalı',
            });
        if ((src === null || src === undefined) !== (at === null || at === undefined))
            issues.push({
                field: 'iysConsentSource',
                message: 'İYS izin tarihi ve kaynağı birlikte girilir',
            });
    }
    for (const [field, settingKey] of Object.entries(CORE_FIELD_SETTING_KEY[type])) {
        if (!opts.requiredSettings.has(settingKey))
            continue;
        if (opts.mode === 'update' && !Object.hasOwn(values, field))
            continue;
        const v = all[field];
        const empty = v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);
        if (empty)
            issues.push({ field, message: REQUIRED_FIELD_MESSAGE });
    }
    return { ok: issues.length === 0, values, issues };
}
// ---------------------------------------------------------------------------
// Detay görünümü uzantısı (`view.profile`, `view.contactInfos`, `view.specialDays`)
// ---------------------------------------------------------------------------
export const contactProfileSchema = z.object({
    kind: z.literal('contact'),
    firstName: z.string(),
    lastName: z.string(),
    title: z.string().nullable(),
    companyId: uuid.nullable(),
    relationTypes: z.array(z.string()),
    gender: z.enum(GENDERS).nullable(),
    details: z.string().nullable(),
    identityNo: z.string().nullable(),
    taxOffice: z.string().nullable(),
    paymentTermDays: z.number().int().nullable(),
    iysConsentAt: z.string().nullable(),
    iysConsentSource: z.string().nullable(),
    sourceKey: z.string().nullable(),
    sectorKey: z.string().nullable(),
    regionKey: z.string().nullable(),
    isActive: z.boolean(),
    primaryEmail: z.string().nullable(),
    primaryPhone: z.string().nullable(),
});
export const companyProfileSchema = z.object({
    kind: z.literal('company'),
    name: z.string(),
    tradeName: z.string().nullable(),
    relationTypes: z.array(z.string()),
    details: z.string().nullable(),
    taxNo: z.string().nullable(),
    taxOffice: z.string().nullable(),
    paymentTermDays: z.number().int().nullable(),
    location: z.object({ lat: z.number(), lng: z.number() }).nullable(),
    sourceKey: z.string().nullable(),
    sectorKey: z.string().nullable(),
    regionKey: z.string().nullable(),
    isActive: z.boolean(),
    primaryEmail: z.string().nullable(),
    primaryPhone: z.string().nullable(),
});
export const crmProfileSchema = z.discriminatedUnion('kind', [
    contactProfileSchema,
    companyProfileSchema,
]);
// ---------------------------------------------------------------------------
// Benzer kayıtlar ve birleştirme önizlemesi (K-14, K-15)
// ---------------------------------------------------------------------------
export const SIMILAR_REASONS = ['name', 'email', 'phone', 'tax_no'];
export const SIMILAR_MAX = 20;
export const similarRecordSchema = z.object({
    id: uuid,
    type: z.enum(CRM_RECORD_TYPES),
    title: z.string(),
    subtitle: z.string().nullable(),
    reasons: z.array(z.enum(SIMILAR_REASONS)),
});
export const similarResponseSchema = z.object({ data: z.array(similarRecordSchema) });
/** Oluşturma öncesi denetim (form): ad + iletişim değerleri + vergi no. Değerler loglanmaz. */
export const similarCheckInputSchema = z
    .object({
    name: z.string().max(250).optional(),
    firstName: z.string().max(100).optional(),
    lastName: z.string().max(100).optional(),
    emails: z.array(z.string().max(500)).max(20).default([]),
    phones: z.array(z.string().max(500)).max(20).default([]),
    taxNo: z.string().max(40).optional(),
    excludeId: uuid.optional(),
})
    .strict();
export const mergePreviewSchema = z.object({
    source: z.object({ id: uuid, title: z.string() }),
    target: z.object({ id: uuid, title: z.string() }),
    moves: z.object({
        contactInfos: z.number().int(),
        contactInfoDuplicates: z.number().int(),
        specialDays: z.number().int(),
        files: z.number().int(),
        tags: z.number().int(),
        sharing: z.number().int(),
        linkedContacts: z.number().int(),
    }),
    irreversible: z.literal(true),
});
// ---------------------------------------------------------------------------
// Etkinlik kaynağı (K-13)
// ---------------------------------------------------------------------------
export const activityItemSchema = z.object({
    id: z.string(),
    at: z.string(),
    actorId: uuid.nullable(),
    actorName: z.string().nullable(),
    /** F4: asistanın eylemi (kullanıcısız; profilin görünen adı, yoksa arayüz "Asistan" yazar). İnsan/sistem eyleminde `null`. */
    assistant: assistantActorSchema.default(null),
    action: z.string(),
    summary: z.string(),
});
export const activityResponseSchema = z.object({
    data: z.array(activityItemSchema),
    nextCursor: z.string().nullable(),
});
export const ACTIVITY_PAGE_MAX = 50;
// ---------------------------------------------------------------------------
// Bildirimler (K-12)
// ---------------------------------------------------------------------------
/** M3: onay talebi (onaylayıcılara) ve not yorumu (not sahibine); aktörün kendisine bildirim yok (ÜK-8) */
export const NOTIFICATION_KINDS = [
    'field_principal_added',
    'approval_requested',
    'feed_comment',
];
/**
 * M3 herkese açık kanallar (migration 0050): web formundan gelen aday (form sorumlusuna) ve müşteri bağlantısından teklif
 * yanıtı (teklif sahibine). `kind` kısıtı genişletilmez (0040 yeniden uygulama tuzağı); ayrım bu konu alanıyla yapılır.
 */
export const NOTIFICATION_TOPICS = [
    'lead_web_form',
    'proposal_link_response',
    /** M3 aday sekansı (K-M3-25): sıradaki adımın bitiş günü geçti (adayın sorumlusuna; migration 0055) */
    'lead_sequence_step_overdue',
    // M4 (migration 0057): aktivite ataması (aktör dışındaki görevlilere)
    'activity_assigned',
    // M4 (migration 0058): destek talebi ataması (aktör dışındaki görevlilere) ve SLA ihlali (görevliler + sahip)
    'ticket_assigned',
    'ticket_sla_breached',
    // P-İ1 T3 (migration 20261028_0380): müşteri yanıtı talebe düştü (görevliler + sahip); durum değiştiyse veride
    'ticket_customer_reply',
    // M5 WP 5.3 (migration 0068): rozet kazanımı (kazanan kullanıcıya; kayda bağlı değil — D3)
    'gamification_badge_earned',
    // v3 C2 (migration 20261010_0100): otomasyon "Bildirim gönder" eylemi (kurala göre kayıt sahibi / seçilen kullanıcılar)
    'automation_notice',
    // v3.1 C0 (migration 20261010_0110): kayıt sorumlusu olarak atanma (yeni sahibe; toplu atamada tek özet bildirim)
    'record_assigned',
    // v3.1 S10 (migration 20261010_0140): sorumlusu olduğunuz konuşmaya müşteriden yeni ileti; kanal sağlığı uyarısı
    // (kanal çalışmıyor / yetki geçersiz — ayar yöneticilerine; kayda bağlı değil)
    'conversation_message',
    'channel_health',
    // v3.1 S9 (migration 20261010_0131): açık bir aday yeni bir başvuruyla tekrar geldi (adayın sorumlusuna)
    'lead_reapplied',
];
export const NOTIFICATION_MAX_RECIPIENTS = 200;
export const NOTIFICATION_PAGE_MAX = 50;
export const notificationItemSchema = z.object({
    id: uuid,
    kind: z.enum(NOTIFICATION_KINDS),
    /** Doluysa bildirimin asıl konusu (metin buna göre kurulur) */
    topic: z.enum(NOTIFICATION_TOPICS).nullable().default(null),
    /** `proposal_link_response`: müşterinin kararı */
    decision: z.enum(['accepted', 'rejected']).nullable().default(null),
    createdAt: z.string(),
    readAt: z.string().nullable(),
    /** Kayıt çağırana görünmüyorsa `null` (başlık ve bağlantı gösterilmez) */
    record: z.object({ type: z.string(), id: uuid, title: z.string() }).nullable(),
    actorName: z.string().nullable(),
    /** Alan etiketi yalnız alıcı alanı okuyabiliyorsa */
    fieldLabel: z.string().nullable(),
    /** `gamification_badge_earned`: kazanılan rozetin adı (kazanım anındaki ad) */
    badgeName: z.string().nullable().default(null),
    /** `automation_notice`: otomasyon iletisi (anahtar kelimeleri eylem anında doldurulmuş) ve kural adı */
    message: z.string().nullable().default(null),
    ruleName: z.string().nullable().default(null),
    /** `record_assigned`: aynı işlemde bu kullanıcıya atanan kayıt sayısı (toplu atamada tek özet bildirim) */
    count: z.number().int().nullable().default(null),
    /** `feed_comment` (TI-013): yorum yapılan haber akışı öğesi — bildirimden doğrudan gönderiye geçiş */
    feedItemId: uuid.nullable().default(null),
});
export const notificationListSchema = z.object({
    data: z.array(notificationItemSchema),
    nextCursor: z.string().nullable(),
    unread: z.number().int(),
});
// ---------------------------------------------------------------------------
// Toplu işlemler (K-19) ve istatistik (§17)
// ---------------------------------------------------------------------------
export const BULK_MAX_RECORDS = 200;
export const bulkRecordActionInputSchema = z.discriminatedUnion('action', [
    z
        .object({
        action: z.literal('set_owner'),
        recordIds: z.array(uuid).min(1).max(BULK_MAX_RECORDS),
        ownerId: uuid,
    })
        .strict(),
    z
        .object({
        action: z.literal('set_active'),
        recordIds: z.array(uuid).min(1).max(BULK_MAX_RECORDS),
        active: z.boolean(),
    })
        .strict(),
    z
        .object({
        action: z.literal('delete'),
        recordIds: z.array(uuid).min(1).max(BULK_MAX_RECORDS),
        /** Açık onay: istemci onay diyalogundan sonra gönderir */
        confirm: z.literal(true),
    })
        .strict(),
]);
export const recordStatsSchema = z.object({
    contactInfos: z.number().int(),
    specialDays: z.number().int(),
    files: z.number().int(),
    /** Çağıranın bu kayıttaki açık hatırlatıcıları (hatırlatıcılar kullanıcıya özeldir) */
    myOpenReminders: z.number().int(),
    tags: z.number().int(),
    /** Yalnız şirket: çağıranın görebildiği bağlı kişiler */
    linkedContacts: z.number().int().nullable(),
    updatedAt: z.string(),
    createdAt: z.string(),
});
export const companyContactsResponseSchema = z.object({
    data: z.array(z.object({
        id: uuid,
        title: z.string(),
        jobTitle: z.string().nullable(),
        primaryEmail: z.string().nullable(),
        primaryPhone: z.string().nullable(),
        isActive: z.boolean(),
    })),
    nextCursor: z.string().nullable(),
});
//# sourceMappingURL=crm.js.map